// Servicio para manejar las peticiones a la API de proyectos
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, setDoc } from 'firebase/firestore';
import { getUserRole, ROLES, generateContractProjectKey } from '../userRoles';
import { fetchWithRetry, checkApiAvailability, getFallbackProjects } from './projectsApiConfig';

/**
 * Obtiene los proyectos y productos desde la API externa
 * @returns {Promise<Array>} Array con los proyectos y productos
 */
export const fetchProjectsAndProducts = async () => {
  try {
    console.log('🔄 Obteniendo proyectos y productos desde la API...');
    
    const apiKey = import.meta.env.VITE_API_KEY;
    
    if (!apiKey) {
      console.error('❌ API key no configurada en variables de entorno');
      console.log('📋 Usando datos de fallback...');
      return getFallbackProjects();
    }
    
    // En desarrollo, usar el proxy de Vite
    const isDevelopment = import.meta.env.DEV;
    const apiUrl = isDevelopment 
      ? `/api/proyectos?apikey=${apiKey}`
      : `${import.meta.env.VITE_API_URL}?apikey=${apiKey}`;
    
    console.log(`📡 Conectando a: ${isDevelopment ? 'proxy local' : import.meta.env.VITE_API_URL}`);
    
    // Verificar disponibilidad de la API primero
    const isApiAvailable = await checkApiAvailability();
    
    if (!isApiAvailable) {
      console.warn('⚠️ API no disponible, usando datos de fallback');
      return getFallbackProjects();
    }
    
    let data = [];
    
    try {
      const response = await fetchWithRetry(apiUrl);
      data = await response.json();
      console.log(`📊 Datos recibidos: ${data.length} registros`);
      
      // Procesar los datos para asignar proyectos a usuarios
      // Ejecutar en segundo plano para no bloquear la carga de la página
      processProjectAssignments(data).catch(err => 
        console.error('Error en proceso de asignación en segundo plano:', err)
      );
      
      return processData(data);
    } catch (error) {
      console.warn('⚠️ Error al conectar con la API después de reintentos:', error.message);
      console.log('📋 Usando datos de fallback...');
      return getFallbackProjects();
    }
  } catch (error) {
    console.error('Error al obtener proyectos y productos:', error);
    console.log('📋 Usando datos de fallback como último recurso...');
    return getFallbackProjects();
  }
};

/**
 * Procesa los datos de la API para asignar proyectos a usuarios
 * @param {Array} data - Datos de la API
 * @returns {Promise<void>}
 */
const processProjectAssignments = async (data) => {
  const LAST_RUN_KEY = 'last_project_assignments_run';
  const ONE_HOUR = 60 * 60 * 1000;

  try {
    // Verificar si ya se ejecutó recientemente
    const lastRun = localStorage.getItem(LAST_RUN_KEY);
    const now = Date.now();
    
    if (lastRun && (now - parseInt(lastRun) < ONE_HOUR)) {
      console.log('⏳ Asignación de proyectos omitida (ejecutada recientemente)');
      return;
    }

    console.log('🔄 Procesando asignaciones de proyectos a usuarios...');
    
    // Extraer correos y proyectos únicos
    const projectEmails = new Map(); // Map de proyecto -> correo
    
    data.forEach(item => {
      const { proyecto, correo } = item;
      if (proyecto && correo) {
        projectEmails.set(proyecto, correo);
      }
    });
    
    // Para cada proyecto con correo, buscar o crear usuario
    for (const [projectName, email] of projectEmails.entries()) {
      await assignProjectToUserByEmail(email, projectName);
    }
    
    console.log(`✅ Asignación de proyectos completada`);
    localStorage.setItem(LAST_RUN_KEY, Date.now().toString());
  } catch (error) {
    console.error('Error al procesar asignaciones de proyectos:', error);
  }
};

/**
 * Asigna un proyecto a un usuario por su correo electrónico
 * @param {string} email - Correo electrónico del usuario
 * @param {string} projectName - Nombre del proyecto a asignar
 * @returns {Promise<boolean>} - Indica si la asignación fue exitosa
 */
const assignProjectToUserByEmail = async (email, projectName) => {
  try {
    if (!email || !projectName) return false;
    
    // Buscar si el usuario ya existe en la base de datos
    const usersRef = collection(db, 'useractas');
    const q = query(usersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    let userId;
    
    if (querySnapshot.empty) {
      // El usuario no existe, crearlo
      console.log(`👤 Creando nuevo usuario para: ${email}`);
      
      // Generar un ID único para el usuario
      userId = email.replace(/[^a-zA-Z0-9]/g, '_');
      
      // Crear el usuario en Firestore
      await setDoc(doc(db, 'useractas', userId), {
        email,
        role: ROLES.USUARIO, // Rol por defecto
        allowedProjects: [projectName],
        createdAt: new Date(),
        createdBy: 'system'
      });
      
      console.log(`✅ Usuario creado y proyecto ${projectName} asignado a ${email}`);
    } else {
      // El usuario existe, actualizar sus proyectos
      userId = querySnapshot.docs[0].id;
      const userData = querySnapshot.docs[0].data();
      
      // Verificar si el proyecto ya está asignado
      const allowedProjects = userData.allowedProjects || [];
      
      if (!allowedProjects.includes(projectName)) {
        // Agregar el proyecto a la lista de proyectos permitidos
        await setDoc(doc(db, 'useractas', userId), {
          allowedProjects: [...allowedProjects, projectName],
          updatedAt: new Date()
        }, { merge: true });
        
        console.log(`✅ Proyecto ${projectName} asignado a usuario existente: ${email}`);
      } else {
        console.log(`ℹ️ El proyecto ${projectName} ya estaba asignado a: ${email}`);
      }
    }
    
    return true;
  } catch (error) {
    console.error(`❌ Error al asignar proyecto ${projectName} a ${email}:`, error);
    return false;
  }
};

/**
 * Obtiene el correo electrónico asociado a un proyecto
 * @param {string} projectName - Nombre del proyecto
 * @returns {Promise<string|null>} - Correo electrónico o null si no se encuentra
 */
export const getProjectEmail = async (projectName) => {
  try {
    if (!projectName) return null;
    
    const apiKey = import.meta.env.VITE_API_KEY;
    const apiUrl = import.meta.env.VITE_API_URL;
    
    if (!apiKey || !apiUrl) {
      console.error('❌ API key o URL no configuradas en variables de entorno');
      return null;
    }
    
    const response = await fetch(`${apiUrl}?apikey=${apiKey}`);
    
    if (!response.ok) {
      throw new Error(`Error en la petición: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Buscar el correo asociado al proyecto
    for (const item of data) {
      if (item.proyecto === projectName && item.correo) {
        return item.correo;
      }
    }
    
    console.warn(`⚠️ No se encontró correo para el proyecto: ${projectName}`);
    return null;
  } catch (error) {
    console.error('Error al obtener correo del proyecto:', error);
    return null;
  }
};

/**
 * Procesa los datos de proyectos y productos para eliminar duplicados
 * @param {Array} data - Datos a procesar
 * @returns {Array} - Datos procesados
 */
const processData = (data) => {
  // Procesar los datos para eliminar duplicados
  const uniqueProjects = new Map();
  
  // Agrupar productos por proyecto
  data.forEach(item => {
    const { proyecto, producto, correo } = item;
    
    if (!uniqueProjects.has(proyecto)) {
      uniqueProjects.set(proyecto, {
        products: new Set(),
        email: correo || null // Guardar el correo asociado al proyecto
      });
    } else if (correo && !uniqueProjects.get(proyecto).email) {
      // Si ya existe el proyecto pero no tiene correo, actualizarlo
      uniqueProjects.get(proyecto).email = correo;
    }
    
    // Agregar el producto al conjunto de productos del proyecto
    if (producto) {
      uniqueProjects.get(proyecto).products.add(producto);
    }
  });
  
  // Convertir el mapa a un array de objetos con el formato esperado
  const result = Array.from(uniqueProjects.entries()).map(([projectName, projectData]) => {
    const { products, email } = projectData;
    const productObj = {};
    
    // Convertir el conjunto de productos a un objeto
    Array.from(products).forEach((product, index) => {
      productObj[`product_${index}`] = product;
    });
    
    return {
      id: projectName, // Usar el nombre del proyecto como ID
      name: projectName,
      email: email, // Incluir el correo en el resultado
      Product: productObj
    };
  });
  
  console.log(`✅ Procesamiento completado: ${result.length} proyectos únicos encontrados`);
  return result;
};

/**
 * Guarda el email personalizado del encargado para un proyecto específico
 * @param {string} projectName - Nombre del proyecto
 * @param {string} email - Email del encargado
 * @returns {Promise<boolean>} - Indica si el guardado fue exitoso
 */
export const saveCustomProjectEmail = async (projectName, email) => {
  try {
    if (!projectName || !email) {
      console.error('❌ Proyecto y email son requeridos para guardar');
      return false;
    }

    // Crear un ID único para el documento basado en el nombre del proyecto
    const docId = projectName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    
    // Guardar en la colección 'project_emails'
    await setDoc(doc(db, 'project_emails', docId), {
      projectName,
      email,
      createdAt: new Date(),
      updatedAt: new Date()
    }, { merge: true });
    
    console.log(`✅ Email personalizado guardado para proyecto ${projectName}: ${email}`);
    return true;
  } catch (error) {
    console.error('❌ Error al guardar email personalizado:', error);
    return false;
  }
};

/**
 * Obtiene el email personalizado del encargado para un proyecto específico
 * @param {string} projectName - Nombre del proyecto
 * @returns {Promise<string|null>} - Email del encargado o null si no se encuentra
 */
export const getCustomProjectEmail = async (projectName) => {
  try {
    if (!projectName) return null;
    
    // Buscar en la colección 'project_emails' por nombre de proyecto
    const projectEmailsRef = collection(db, 'project_emails');
    const q = query(projectEmailsRef, where('projectName', '==', projectName));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      console.log(`✅ Email personalizado encontrado para proyecto ${projectName}: ${data.email}`);
      return data.email;
    }
    
    console.log(`ℹ️ No se encontró email personalizado para proyecto: ${projectName}`);
    return null;
  } catch (error) {
    console.error('❌ Error al obtener email personalizado:', error);
    return null;
  }
};

/**
 * Guarda el email personalizado del encargado para una combinación contrato/proyecto específica
 * @param {Array} proyectos - Array de nombres de proyectos
 * @param {Array} productos - Array de nombres de productos
 * @param {string} email - Email del encargado
 * @returns {Promise<boolean>} - Indica si el guardado fue exitoso
 */
export const saveCustomContractProjectEmail = async (proyectos, productos, email) => {
  try {
    if (!proyectos || proyectos.length === 0 || !email) {
      console.error('❌ Proyectos y email son requeridos para guardar');
      return false;
    }

    // Usar el formato de generateContractProjectKey para cada combinación
    const contractProjectKeys = [];
    
    // Si hay productos, crear combinaciones contrato|proyecto para cada uno
    if (productos && productos.length > 0) {
      for (const proyecto of proyectos) {
        for (const producto of productos) {
          contractProjectKeys.push(generateContractProjectKey(proyecto, producto));
        }
      }
    } else {
      // Si no hay productos, usar solo los proyectos
      contractProjectKeys.push(...proyectos);
    }
    
    // Guardar cada combinación por separado
    const savePromises = contractProjectKeys.map(async (key) => {
      const docId = key.replace(/[^a-zA-Z0-9|]/g, '_').toLowerCase();
      
      await setDoc(doc(db, 'project_emails', docId), {
        contractProjectKey: key,
        proyectos,
        productos: productos || [],
        email,
        projectName: key, // Agregar projectName que coincida con allowedContractProjects
        createdAt: new Date(),
        updatedAt: new Date()
      }, { merge: true });
      
      console.log(`✅ Email personalizado guardado para ${key}: ${email}`);
    });
    
    await Promise.all(savePromises);
    return true;
  } catch (error) {
    console.error('❌ Error al guardar email personalizado para combinación:', error);
    return false;
  }
};

/**
 * Obtiene el email personalizado del encargado para una combinación contrato/proyecto específica
 * @param {Array} proyectos - Array de nombres de proyectos
 * @param {Array} productos - Array de nombres de productos
 * @returns {Promise<string|null>} - Email del encargado o null si no se encuentra
 */
export const getCustomContractProjectEmail = async (proyectos, productos) => {
  try {
    if (!proyectos || proyectos.length === 0) return null;
    
    // Buscar usando el formato de generateContractProjectKey
    const contractProjectKeys = [];
    
    // Si hay productos, crear combinaciones contrato|proyecto para cada uno
    if (productos && productos.length > 0) {
      for (const proyecto of proyectos) {
        for (const producto of productos) {
          contractProjectKeys.push(generateContractProjectKey(proyecto, producto));
        }
      }
    } else {
      // Si no hay productos, usar solo los proyectos
      contractProjectKeys.push(...proyectos);
    }
    
    // Buscar en la colección 'project_emails' por cualquiera de las claves
    const projectEmailsRef = collection(db, 'project_emails');
    
    for (const key of contractProjectKeys) {
      const q = query(projectEmailsRef, where('contractProjectKey', '==', key));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        console.log(`✅ Email personalizado encontrado para ${key}: ${data.email}`);
        return data.email;
      }
    }
    
    console.log(`ℹ️ No se encontró email personalizado para ninguna combinación: ${contractProjectKeys.join(', ')}`);
    return null;
  } catch (error) {
    console.error('❌ Error al obtener email personalizado para combinación:', error);
    return null;
  }
};

/**
 * Obtiene el correo electrónico asociado a un proyecto (primero busca personalizado, luego API)
 * @param {string} projectName - Nombre del proyecto
 * @returns {Promise<string|null>} - Correo electrónico o null si no se encuentra
 */
export const getProjectEmailWithFallback = async (projectName) => {
  try {
    if (!projectName) return null;
    
    // Primero intentar obtener email personalizado
    const customEmail = await getCustomProjectEmail(projectName);
    if (customEmail) {
      return customEmail;
    }
    
    // Si no hay email personalizado, usar la función original de la API
    return await getProjectEmail(projectName);
  } catch (error) {
    console.error('Error al obtener correo del proyecto con fallback:', error);
    return null;
  }
};

/**
 * Obtiene el correo electrónico asociado a una combinación contrato/proyecto (primero busca personalizado, luego API)
 * @param {Array} proyectos - Array de nombres de proyectos
 * @param {Array} productos - Array de nombres de productos
 * @returns {Promise<string|null>} - Correo electrónico o null si no se encuentra
 */
export const getContractProjectEmailWithFallback = async (proyectos, productos) => {
  try {
    if (!proyectos || proyectos.length === 0) return null;
    
    // Primero intentar obtener email personalizado para la combinación
    const customEmail = await getCustomContractProjectEmail(proyectos, productos);
    if (customEmail) {
      return customEmail;
    }
    
    // Si no hay email personalizado para la combinación, intentar con el primer proyecto
    const firstProject = proyectos[0];
    return await getProjectEmailWithFallback(firstProject);
  } catch (error) {
    console.error('Error al obtener correo de la combinación contrato/proyecto con fallback:', error);
    return null;
  }
};